import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { turnFailureAction, turnFailurePayload } from '@/api/types_session'
import { useAgentLauncher } from '@/lib/agentLauncher'
import { ErrorMessageCard } from './ErrorMessageCard'

interface TurnFailureCardProps {
  /** The turn_failure record's payload */
  payload: unknown
  /** The agent whose turn failed: the record's performer */
  agentSlug: string
  onRetry?: () => void
  isRetrying?: boolean
}

/**
 * A durable terminal failure (tricorder-session/ADR-0019): what went wrong and
 * the options that resolve it, in the reader's language. The kind decides what
 * this client can do about it — a retry only where a retry can change the
 * outcome, a new conversation where the session's agent version cannot run —
 * and a record without the contract's shape is shown as unreadable, with the
 * new conversation its text tells the reader to start.
 */
export const TurnFailureCard = memo(function TurnFailureCard({
  payload,
  agentSlug,
  onRetry,
  isRetrying = false,
}: TurnFailureCardProps) {
  const { t } = useTranslation()
  const launcher = useAgentLauncher()
  const failure = turnFailurePayload(payload)
  const action = failure ? turnFailureAction(failure.failure) : 'new_conversation'
  const onNewConversation =
    action === 'new_conversation' && launcher?.canLaunch(agentSlug)
      ? () => launcher.launch(agentSlug)
      : undefined

  if (!failure) {
    return <ErrorMessageCard message={t('chat.errors.unreadableFailure')} onNewConversation={onNewConversation} />
  }
  return (
    <ErrorMessageCard
      message={failure.reason}
      options={failure.options}
      onRetry={action === 'retry' ? onRetry : undefined}
      isRetrying={isRetrying}
      onNewConversation={onNewConversation}
    />
  )
})
